import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type Ref,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Card } from '../cards';
import { useNoteTools } from './model/hooks';
import st from './styles.module.scss';
import { NoteEditorTools } from './tools';
import { EditorRefContext } from './utils';
import { VideoUrlModal } from '../forms';

interface Props {
  editValue?: string;
  customStyles?: React.CSSProperties;
}

export const NoteEditor = forwardRef<HTMLDivElement, Props>(
  ({ editValue, customStyles }, ref: Ref<HTMLDivElement>) => {
    const defaultToolsPosition = 18;
    const [note, setNote] = useState<string | null>(null);
    const [positionY, setPositionY] = useState(defaultToolsPosition);
    const localEditorRef = useRef<HTMLDivElement>(null);
    const savedSelection = useRef<Range | null>(null);
    const location = useLocation();

    const {
      isOpenTools,
      switchOpenTools,
      setEditState,
      isVisibleTools,
      setVisibleTools,
      isUrlModalOpen,
      setUrlModalOpen,
    } = useNoteTools();

    useImperativeHandle(ref, () => localEditorRef.current as HTMLDivElement, [localEditorRef]);

    useEffect(() => {
      if (location.pathname.includes('new')) {
        setVisibleTools(true);
        localEditorRef.current?.focus();
      }
    }, []);

    useEffect(() => {
      if (editValue) {
        setNote(editValue);
      }
    }, [editValue]);

    useEffect(() => {
      if (isVisibleTools) {
        setToolsPosition();
      }
    }, [isVisibleTools, positionY]);

    const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
      const editorElement = e.currentTarget;

      if (e.key === 'Backspace') {
        if (editorElement.innerHTML === '' || editorElement.innerHTML === '<br>') {
          insertParagraph(editorElement);
        }

        if (editorElement.textContent?.length === 1) {
          setVisibleTools(true);
        }
      }

      if (e.key === 'Enter') {
        setEditState('normal');
        if (isOpenTools) {
          switchOpenTools();
        }
        e.preventDefault();
        insertParagraph(editorElement);
      }
      if (
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight'
      ) {
        setVisibleTools(false);
      }
    };

    const handleClickEditor: MouseEventHandler<HTMLDivElement> = (e) => {
      setVisibleTools(false);
      const editor = localEditorRef.current;
      const innerClickElem = (e.target as HTMLElement).innerHTML;
      if (editor) {
        const innerEditor = editor.innerHTML.trim();

        if (innerEditor === '' || innerEditor === '<br>') {
          insertParagraph(editor);
        }
        if (innerClickElem === '<span></span><br>' || innerClickElem === '<span></span>&nbsp;') {
          setToolsPosition();
          setVisibleTools(true);
        }
      }
    };

    const handleInput = () => {
      if (
        isVisibleTools &&
        localEditorRef.current &&
        localEditorRef.current?.innerText.length > 1
      ) {
        setVisibleTools(false);
      }
    };

    function insertParagraph(editor: HTMLDivElement) {
      const selection = document.getSelection();
      if (!selection || selection.rangeCount === 0) {
        return;
      }

      const range = selection.getRangeAt(0);
      const currentNode = range.startContainer;
      const currentParagraph =
        currentNode.nodeType === Node.ELEMENT_NODE && (currentNode as HTMLElement).tagName === 'P'
          ? currentNode
          : currentNode.parentNode;

      const paragraph = document.createElement('p');
      const span = document.createElement('span');
      const textNode = document.createTextNode('\u00A0');
      paragraph.appendChild(span);
      paragraph.appendChild(textNode);

      if (
        currentParagraph &&
        currentParagraph.nodeType === Node.ELEMENT_NODE &&
        (currentParagraph as HTMLElement).tagName === 'P'
      ) {
        // Вставляем параграф после текущего узла
        currentParagraph.parentNode?.insertBefore(paragraph, currentParagraph.nextSibling);
      } else {
        // Если текущий узел не параграф, вставляем первый параграф
        editor.append(paragraph);
      }

      range.setStart(textNode, 1);
      range.setEnd(textNode, 1);

      selection.removeAllRanges();
      selection.addRange(range);

      editor.focus();
      setToolsPosition();
      setVisibleTools(true);
    }

    const setToolsPosition = () => {
      const activeSelection = document.getSelection();

      if (activeSelection && activeSelection.rangeCount !== 0) {
        const rangeRect = activeSelection.getRangeAt(0).getBoundingClientRect();
        const parentRect = localEditorRef.current?.getBoundingClientRect();

        if (!parentRect) {
          return null;
        }

        if (rangeRect.top === 0) {
          setPositionY((prev) => defaultToolsPosition);
        }
        if (rangeRect.top > parentRect.top) {
          const y = rangeRect.top - parentRect.top;
          setPositionY((prev) => y);
        }
      }
    };

    const insertLink = (link: string) => {
      if (!savedSelection.current) {
        return;
      }

      const range = savedSelection.current;
      const linkElem = document.createElement('a');
      linkElem.href = link;
      linkElem.textContent = link;
      range.deleteContents();
      range.insertNode(linkElem);

      range.setStartAfter(linkElem);
      range.setEndAfter(linkElem);

      const selection = document.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);

      setVisibleTools(false);
      localEditorRef.current?.focus();
    };

    const saveCurrentSelection = () => {
      const selection = document.getSelection();
      if (selection && selection.rangeCount > 0) {
        savedSelection.current = selection.getRangeAt(0);
      }
    };

    return (
      <>
        <div className={st.wrapper}>
          {isVisibleTools && (
            <EditorRefContext.Provider value={localEditorRef}>
              <NoteEditorTools positionY={positionY} />
            </EditorRefContext.Provider>
          )}

          <Card className={st.cardEditor} style={customStyles}>
            <div
              ref={localEditorRef}
              contentEditable="true"
              onKeyDown={handleKeyDown}
              onClick={handleClickEditor}
              onInput={handleInput}
              className={st.editor}
              onFocus={saveCurrentSelection}
              onBlur={saveCurrentSelection}
              dangerouslySetInnerHTML={{ __html: note ?? '<p><span></span><br></p>' }}
            ></div>
          </Card>
        </div>
        <VideoUrlModal
          placeholder="Вставьте ссылку"
          isOpen={isUrlModalOpen}
          close={() => setUrlModalOpen(false)}
          saveUrl={(url) => insertLink(url)}
        />
      </>
    );
  }
);
